import { axiosClientAdmin } from '@/lib/auth';
import { Comparator, Filter, FilterGroup, FilterOperator, SortOrder } from '@/lib/utils';
import { EnumContactPreference } from '../Alert/types';
import { ConnectedDeviceFilterChoice } from '@/lib/integrationTypes/externalConnectionTypes';
import { AddressDto } from '@/lib/api/types.gen';

export interface SitesSearchDto {
  id: string;
  name: string;
  companyId: string;
  companyName: string;
  address1?: string;
  isActive?: boolean;
  archivedAt?: string | null;
  issues?: number;
}

export interface SitesSearched {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
  totalRows?: number;
  rows?: SitesSearchDto[];
  nextPage?: number;
}

export interface ProjectItemDto {
  id?: string;
  onSightDeviceId?: string;
  type?: string;
  name?: string;
}

export interface ProjectScheduleDto {
  projectId?: string;
  blockSize?: number;
  blocks?: BlockDto[];
}

export interface BlockDto {
  dayOfWeek: number;
  blockIdx: number;
  active: boolean;
}

export enum EnumArmingEventType {
  'Armed' = 'Armed',
  'Disarmed' = 'Disarmed',
}

export enum EnumSirenProfile {
  'All' = 'All',
  'None' = 'None',
  'Intruder' = 'Intruder',
}

export enum EnumArmProfile {
  'Auto' = 'Auto',
  'Armed' = 'Armed',
  'Disarmed' = 'Disarmed',
  'TestMode' = 'TestMode',
}

export enum EnumAiAutomationLevel {
  'None' = 'None',
  'Assistant' = 'Assistant',
  'Full' = 'Full',
}

export interface ProjectMonitoringDto {
  monitoringCompanyName?: string;
  monitoringCompanyId?: string;
  externalId?: string;
  order?: number;
}

export enum EnumVideoProcessor {
  'UmboCv' = 'UmboCv',
  'ThreeDEye' = 'ThreeDEye',
  'Smtp' = 'Smtp',
  'Milestone' = 'Milestone',
  'Camect' = 'Camect',
  'NxWitness' = 'NxWitness',
  'HikVisionNvr' = 'HikVisionNvr',
  'DahuaCamera' = 'DahuaCamera',
  'OpenEye' = 'OpenEye',
}

export interface AssetVideoProcessorDto {
  videoProcessor?: EnumVideoProcessor;
  assetName?: string;
  assetSerial?: string;
  assetId?: string;
  videoProcessorId?: string;
  videoProcessorConfigId?: string;
  description?: string;
  nvrDeviceName?: string;
}

export interface UserNotificationDto {
  setting?: string;
  email?: boolean;
  push?: boolean;
  sms?: boolean;
}

export enum DetectionLabel {
  'Person' = 'Person',
  'Vehicle' = 'Vehicle',
}

export interface MonitoringZoneDto {
  id?: string;
  projectId?: string;
  description?: string;
  videoProcessors?: AssetVideoProcessorDto[];
  schedule?: ProjectScheduleDto;
}

export interface ProjectFullDto {
  id?: string;
  name?: string;
  companyId?: string;
  companyName?: string;
  onSightLogicGroupId?: string;
  items?: ProjectItemDto[];
  newMonitoringAlertsAfterSeconds?: number;
  disarmedUntil?: Date;
  disarmedBy?: string;
  noThreatDisarmMinutes?: number;
  schedule?: ProjectScheduleDto;
  timeZone?: string;
  armedByCalendar?: EnumArmingEventType;
  sirenProfile?: EnumSirenProfile;
  armProfile?: EnumArmProfile;
  siteNotes?: string;
  guardResponseEnabled?: boolean;
  sirenCooldownSeconds?: number;
  sirensUnlinked?: boolean;
  complianceEnabled?: boolean;
  monitoringCompanies?: ProjectMonitoringDto[];
  guardResponseDispatchCode?: string;
  sirenProfilePerson?: EnumSirenProfile;
  sirenProfileVehicle?: EnumSirenProfile;
  address?: AddressDto;
  nvRecorderIds?: string[];
  aiPrimer?: string;
  noThreatAiPrimer?: string;
  aiAutomationLevel?: EnumAiAutomationLevel;
  userNotifications?: UserNotificationDto[];
  detectionLabels?: DetectionLabel[];
  objectDetectionSensitivity?: number;
  availableVideoProcessors?: AssetVideoProcessorDto[];
  monitoringZones?: MonitoringZoneDto[];
  externalServiceOptions?: Record<string, ConnectedDeviceFilterChoice>;
  dispositionWorkflowConfig?: DispositionWorkflowConfig;
  disabledDispositions?: string[];
  archivedAt?: string | null;
  defaultRouteNamespaceId?: string | null;
}

export interface Step {
  stepNumber: number;
  hasNote: boolean;
  hasCheckbox: boolean;
  title: string;
  instructions?: string;
}

export interface TimeRange {
  start: string; // Format: "HH:MM:SS"
  end: string; // Format: "HH:MM:SS"
}

export interface DaySchedule {
  day: string; // "Sunday", "Monday", etc.
  timeRanges: TimeRange[];
}

export interface WeeklySchedule {
  days: DaySchedule[];
}

export interface WorkflowConfig {
  name: string;
  steps: Step[];
  schedule?: WeeklySchedule;
  isDefault: boolean;
}

export interface DispositionWorkflowConfig {
  [status: string]: WorkflowConfig[];
}

export interface BackendDispositionStep {
  hasNote?: boolean;
  hasCheckbox?: boolean;
  title?: string;
  instructions?: string;
}

export interface SiteInfoSearched {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
  project?: ProjectFullDto;
}

export interface CreateSiteForm {
  name: string;
}

export interface ProjectCrudded {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
  project?: ProjectDto;
}

export interface DashboardName {
  id?: string;
  name?: string;
}

export interface ProjectDto {
  id?: string;
  companyId?: string;
  name?: string;
  onSightLogicGroupId?: string;
  newMonitoringAlertsAfterSeconds?: number;
  dashboards?: DashboardName[];
  sirenProfile?: EnumSirenProfile;
  armProfile?: EnumArmProfile;
  siteNotes?: string;
  guardResponseEnabled?: boolean;
  sirenCooldownSeconds?: number;
  sirensUnlinked?: boolean;
  complianceEnabled?: boolean;
  guardResponseDispatchCode?: string;
  sirenProfilePerson?: EnumSirenProfile;
  sirenProfileVehicle?: EnumSirenProfile;
  timeZone?: string;
  address?: AddressDto;
  aiPrimer?: string;
  noThreatAiPrimer?: string;
  detectionLabels?: DetectionLabel[];
  objectDetectionSensitivity?: number;
  aiAutomationLevel?: EnumAiAutomationLevel;
  archivedAt?: string | null;
}

export interface ProjectUpdateForm {
  name?: string;
  onSightLogicGroupId?: string;
  newMonitoringAlertsAfterSeconds?: number;
  dashboards?: DashboardName[];
  sirenProfile?: EnumSirenProfile;
  armProfile?: EnumArmProfile;
  siteNotes?: string;
  guardResponseEnabled?: boolean;
  sirenCooldownSeconds?: number;
  sirensUnlinked?: boolean;
  complianceEnabled?: boolean;
  guardResponseDispatchCode?: string;
  sirenProfilePerson?: EnumSirenProfile;
  sirenProfileVehicle?: EnumSirenProfile;
  timeZone?: string;
  address?: AddressDto;
  aiPrimer?: string;
  noThreatAiPrimer?: string;
  detectionLabels?: DetectionLabel[];
  objectDetectionSensitivity?: number;
  aiAutomationLevel?: EnumAiAutomationLevel;
  disabledDispositions?: string[];
  archivedAt?: string | null;
  defaultRouteNamespaceId?: string | null;
}

export interface NoThreatSet {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
}

export interface SetNoThreatForm {
  disarmTime?: number;
}

export interface SetMonitoringCompanyForm {
  externalId?: string;
  order?: number;
}

export interface ProjectMonitoringCompanySet {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
}

export const fetchSitesMonitoring = async (
  params: {
    pageNumber?: number;
    pageSize?: number;
    sortBy?: string;
    sortDir?: SortOrder;
    filterOperator?: FilterOperator;
    filterFilters?: Filter[];
    filterFilterGroups?: FilterGroup[];
  } = {}
) => {
  let requestParams = {
    PageNumber: params['pageNumber'],
    PageSize: params['pageSize'],
    SortBy: params['sortBy'],
    SortDir: params['sortDir'],
    'Filter.Operator': params['filterOperator'],
    'Filter.Filters': params['filterFilters'],
    'Filter.FilterGroups': params['filterFilterGroups'],
  };

  const response = await axiosClientAdmin.get<SitesSearched>('/Monitoring/projects/search', {
    params: requestParams,
  });

  const sites = response.data.rows || [];

  return {
    sites,
    nextPage:
      (response.data.totalRows || 0) > params.pageNumber! * params.pageSize!
        ? params.pageNumber! + 1
        : undefined,
    totalRows: response.data.totalRows || 0,
  };
};

export const fetchSite = async (siteId: string, companyId: string): Promise<ProjectFullDto> => {
  try {
    if (!siteId || !companyId) {
      throw new Error('Site ID and Company ID are required');
    }

    const response = await axiosClientAdmin.get<SiteInfoSearched>(
      `/Customer/${companyId}/project/${siteId}`,
      { params: {} }
    );

    if (!response.data) {
      throw new Error('No data received from server');
    }

    const site = response.data.project;
    if (!site) {
      throw new Error('Site not found');
    }

    return site;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    // Handle specific axios errors
    if (error.response) {
      // Server responded with error status
      switch (error.response.status) {
        case 401:
          throw new Error('Unauthorized access. Please log in again.');
        case 403:
          throw new Error('You do not have permission to access this site.');
        case 404:
          throw new Error('Site not found.');
        default:
          throw new Error(`Server error: ${error.response.data?.message || 'Unknown error'}`);
      }
    } else if (error.request) {
      // Request made but no response received
      throw new Error('No response from server. Please check your connection.');
    } else {
      // Something else went wrong
      throw new Error(error.message || 'Failed to fetch site information');
    }
  }
};

export const createSite = async (
  customerId: string,
  data: CreateSiteForm
): Promise<ProjectCrudded> => {
  try {
    const response = await axiosClientAdmin.post<ProjectCrudded>(
      `/Customer/${customerId}/project`,
      data
    );

    if (!response.data) {
      throw new Error('No data received from server');
    }

    return response.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data?.message || 'Failed to create site');
    }
    throw new Error('Failed to create site');
  }
};

export const updateSite = async (
  customerId: string,
  projectId: string,
  data: ProjectUpdateForm
): Promise<ProjectCrudded> => {
  try {
    const response = await axiosClientAdmin.post<ProjectCrudded>(
      `/Customer/${customerId}/project/${projectId}`,
      data
    );

    if (!response.data) {
      throw new Error('No data received from server');
    }

    return response.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data?.message || 'Failed to update site');
    }
    throw new Error('Failed to update site');
  }
};

export const archiveSite = async (
  customerId: string,
  projectId: string,
  site: ProjectFullDto,
  archive: boolean
): Promise<ProjectCrudded> => {
  return updateSite(customerId, projectId, {
    ...site,
    armProfile: archive ? EnumArmProfile.Disarmed : site.armProfile,
    archivedAt: archive ? new Date().toISOString() : null,
  });
};

export const updateNoThreatDisposition = async (
  customerId: string,
  projectId: string,
  data: SetNoThreatForm
): Promise<NoThreatSet> => {
  try {
    const response = await axiosClientAdmin.post<NoThreatSet>(
      `/Customer/${customerId}/project/${projectId}/nothreat`,
      data
    );

    if (!response.data) {
      throw new Error('No data received from server');
    }

    return response.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data?.message || 'Failed to update no threat disposition');
    }
    throw new Error('Failed to update no threat disposition');
  }
};

export interface ProjectScheduleDto {
  projectId?: string;
  blockSize?: number;
  blocks?: BlockDto[];
}

export interface ProjectScheduleSet {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
  project?: ProjectCrudded;
}

export const postUpdateProjectScheduleCustomer = async (
  customerId: string,
  projectId: string,
  body: ProjectScheduleDto
): Promise<ProjectScheduleSet> => {
  try {
    const response = await axiosClientAdmin.post<ProjectScheduleSet>(
      `/Customer/${customerId}/project/${projectId}/schedule`,
      body
    );

    if (!response.data) {
      throw new Error('No data received from server');
    }

    return response.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data?.message || 'Failed to update site schedule');
    }
    throw new Error('Failed to update site schedule');
  }
};

export interface MonitoringAlertDto {
  id: string;
  projectId: string;
  alertId: string;
  projectName: string;
  notified: boolean;
  notifiedAt: string;
  lastEvent: string;
  actionedBy: string | null;
  actionedAt: string | null;
  actioned: boolean;
  closedAt: string | null;
  nextActionSlaDue: string | null;
  companyId: string;
  companyName: string;
  occurred: string;
  status: string;
  isClosed: boolean;
  hadSlaBreach: boolean;
  snapshot: string | null;
  events: any | null;
  snapshotValueId: string | null;
}

export interface MonitoringAlertsSearched {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
  totalRows?: number;
  rows?: MonitoringAlertDto[];
}

export async function updateMonitoringCompany(
  customerId: string,
  projectId: string,
  monitoringCompanyId: string,
  data?: SetMonitoringCompanyForm
): Promise<ProjectMonitoringCompanySet> {
  const response = await axiosClientAdmin.post(
    `/Customer/${customerId}/project/${projectId}/monitoringcompany/${monitoringCompanyId}`,
    data
  );
  return response.data;
}

export async function removeMonitoringCompany(
  customerId: string,
  projectId: string,
  monitoringCompanyId: string
): Promise<ProjectMonitoringCompanySet> {
  const response = await axiosClientAdmin.delete(
    `/Customer/${customerId}/project/${projectId}/monitoringcompany/${monitoringCompanyId}`
  );
  return response.data;
}

export enum CurrentArmState {
  'Armed' = 'Armed',
  'Disarmed' = 'Disarmed',
  'TestMode' = 'TestMode',
}

export interface ArmStatusFetched {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
  status?: CurrentArmState;
  causedBy?: string;
  extraInfo?: string;
}

export async function getArmStatus(
  customerId: string,
  projectId: string
): Promise<ArmStatusFetched> {
  try {
    if (!customerId || !projectId) {
      throw new Error('Customer ID and Project ID are required');
    }

    const response = await axiosClientAdmin.get<ArmStatusFetched>(
      `/Customer/${customerId}/project/${projectId}/armstatus`
    );

    if (!response.data) {
      throw new Error('No data received from server');
    }

    return response.data;
  } catch (error) {
    console.error('Error getting arm status:', error);
    throw error;
  }
}

export interface ProjectContactDto {
  id?: string;
  projectId?: string;
  name?: string;
  email?: string;
  phone?: string;
  order?: number;
  role?: string;
  contactPreference?: EnumContactPreference;
  companyId?: string;
  companyName?: string;
  lastName?: string;
  contactId?: string;
  contactCompany?: string;
}

export interface ProjectContactsSearched {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
  totalRows?: number;
  rows?: ProjectContactDto[];
  nextPage?: number;
}

export interface ProjectContactCrudded {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
}

export interface ContactDto {
  id?: string;
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  company?: string;
}

export interface ContactsSearched {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
  totalRows?: number;
  rows?: ContactDto[];
}

export async function getSiteContacts(
  customerId: string,
  projectId: string,
  params: {
    pageNumber?: number;
    pageSize?: number;
    sortBy?: string;
    sortDir?: SortOrder;
    filterOperator?: FilterOperator;
    filterFilters?: Filter[];
    filterFilterGroups?: FilterGroup[];
  } = {}
): Promise<ProjectContactsSearched> {
  let requestParams = {
    PageNumber: params['pageNumber'],
    PageSize: params['pageSize'],
    SortBy: params['sortBy'],
    SortDir: params['sortDir'],
    'Filter.Operator': params['filterOperator'],
    'Filter.Filters': params['filterFilters'],
    'Filter.FilterGroups': params['filterFilterGroups'],
  };

  const response = await axiosClientAdmin.get<ProjectContactsSearched>(
    `/Customer/${customerId}/project/${projectId}/contacts`,
    {
      params: requestParams,
    }
  );

  const contacts = response.data || [];

  return contacts;
}

export const addSiteContact = async (
  customerId: string,
  projectId: string,
  body?: ProjectContactDto
): Promise<ProjectContactCrudded> => {
  try {
    const response = await axiosClientAdmin.post<ProjectContactCrudded>(
      `/Customer/${customerId}/project/${projectId}/contact`,
      body
    );

    if (!response.data) {
      throw new Error('No data received from server');
    }

    return response.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data?.message || 'Failed to add site contact');
    }
    throw new Error('Failed to add site contact');
  }
};

export const updateSiteContact = async (
  customerId: string,
  projectId: string,
  contactId: string,
  body?: ProjectContactDto
): Promise<ProjectContactCrudded> => {
  try {
    const response = await axiosClientAdmin.post<ProjectContactCrudded>(
      `/Customer/${customerId}/project/${projectId}/contact/${contactId}`,
      body
    );

    if (!response.data) {
      throw new Error('No data received from server');
    }

    return response.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data?.message || 'Failed to update site contact');
    }
    throw new Error('Failed to update site contact');
  }
};

export const deleteSiteContact = async (
  customerId: string,
  projectId: string,
  contactId: string
): Promise<ProjectContactCrudded> => {
  try {
    const response = await axiosClientAdmin.delete<ProjectContactCrudded>(
      `/Customer/${customerId}/project/${projectId}/contact/${contactId}`
    );

    if (!response.data) {
      throw new Error('No data received from server');
    }

    return response.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data?.message || 'Failed to delete site contact');
    }
    throw new Error('Failed to delete site contact');
  }
};

export const searchSiteContact = async (search?: string): Promise<ContactDto[]> => {
  try {
    // const response = await axiosClientAdmin.get<ContactsSearched>(`/Customer/contacts/search`);

    // console.log('Response', response);

    const response = await axiosClientAdmin.get<ContactsSearched>(`/Customer/contacts/search`, {
      params: {
        'Filter.FilterGroups': {
          filterFilterGroups: [
            {
              operator: FilterOperator.Or,
              filters: [
                {
                  field: 'email',
                  value: search,
                  action: Comparator.ILike,
                },
                {
                  field: 'firstName',
                  value: search,
                  action: Comparator.ILike,
                },
                {
                  field: 'lastName',
                  value: search,
                  action: Comparator.ILike,
                },
              ],
            },
          ],
        },
      },
    });

    if (!response.data) {
      throw new Error('No data received from server');
    }

    return response.data.rows || [];
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data?.message || 'Failed to search site contact');
    }
    throw new Error('Failed to search site contact');
  }
};
